/**
 * What a school pays, worked out from the catalog (ADR 0009, amendment). The public price page
 * and the billing page's school form both show this, so they cannot disagree, and it is the same
 * arithmetic an invoice uses: the price per student in øre times the number of students.
 */
import { PLANS, planPrice } from './catalog'

/** Danish VAT, added on top of every price for customers in Denmark (ADR 0009, Decision 8). */
export const DANISH_VAT_PERCENT = 25

/** More students than this is not a number anybody types; it keeps every sum a safe integer. */
const MAX_REQUESTED = 1_000_000_000

export interface SchoolQuote {
  /** What was asked for, as a whole number of students (never negative). */
  requestedStudents: number
  /** What the invoice is made for: the request, but at least the minimum. */
  billedStudents: number
  /** The request was below the minimum, so the minimum is what is billed. */
  minimumApplied: boolean
  /** The school can order this number itself (from the minimum up to the maximum). */
  orderable: boolean
  minStudents: number
  maxStudents: number
  /** In øre, excluding VAT. */
  unitAmount: number
  /** In øre, excluding VAT, per year: `billedStudents * unitAmount`, as on the invoice. */
  total: number
  /** What each of the requested students costs in øre (rounded); null when nobody was asked for. */
  perRequestedStudent: number | null
  /** In øre, rounded to whole kroner: the year divided by twelve. */
  monthlyEquivalent: number
  creditsPerStudent: number
  credits: number
  /** In øre. */
  vat: number
  /** In øre: `total` plus Danish VAT. */
  totalInclVat: number
}

/** A number of students as typed: anything that is not a number counts as nobody. */
export function wholeStudents(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(MAX_REQUESTED, Math.max(0, Math.trunc(value)))
}

export function schoolQuote(students: number): SchoolQuote {
  const price = planPrice('school', 'year')
  const limits = PLANS.school.perStudent
  if (!price || !limits) throw new Error('The catalog has no Skole price')

  const requestedStudents = wholeStudents(students)
  const billedStudents = Math.max(limits.min, requestedStudents)
  const total = billedStudents * price.unitAmount
  const vat = Math.round((total * DANISH_VAT_PERCENT) / 100)

  return {
    requestedStudents,
    billedStudents,
    minimumApplied: requestedStudents < limits.min,
    orderable: billedStudents <= limits.max,
    minStudents: limits.min,
    maxStudents: limits.max,
    unitAmount: price.unitAmount,
    total,
    perRequestedStudent: requestedStudents > 0 ? Math.round(total / requestedStudents) : null,
    monthlyEquivalent: Math.round(total / 1200) * 100,
    creditsPerStudent: price.credits,
    credits: billedStudents * price.credits,
    vat,
    totalInclVat: total + vat,
  }
}
